import * as React from 'react';
import { Box, Breadcrumbs, Link, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { MapPin as MapPinIcon } from '@phosphor-icons/react/dist/ssr/MapPin';
import { HouseLine as HouseLineIcon } from '@phosphor-icons/react/dist/ssr/HouseLine';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';
import { RouterLink } from '@/components/core/v2/link';

interface UnitHeaderProps {
  unit?: Unit | null;
  isLoading: boolean;
}

export function UnitHeader({ unit, isLoading }: UnitHeaderProps): React.JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} variant="text" width={160} />
        <Skeleton height={28} variant="text" width="45%" />
        <Stack direction="row" spacing={1}>
          <Skeleton height={14} variant="text" width={180} />
        </Stack>
      </Stack>
    );
  }

  const fullLocation = [unit?.city, unit?.state, unit?.zip_code].filter(Boolean).join(', ');

  return (
    <Stack spacing={1.5}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" sx={{ fontSize: '0.8125rem' }}>
        <Link
          color="text.secondary"
          component={RouterLink}
          href={paths.units.index}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: 'text.primary' } }}
          underline="hover"
        >
          <ArrowLeftIcon size={14} />
          Units
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
          {unit?.address || 'Unit Details'}
        </Typography>
      </Breadcrumbs>

      {/* Title */}
      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}>
        {unit?.address || 'Unit Details'}
        {unit?.unit_number ? ` — Unit ${unit.unit_number}` : ''}
      </Typography>

      {/* Meta row */}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {fullLocation && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <MapPinIcon color="var(--mui-palette-text-disabled)" size={14} />
            <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              {fullLocation}
            </Typography>
          </Stack>
        )}
        {unit?.is_rental && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: '2px',
              borderRadius: 1,
              bgcolor: '#6366F118',
              border: '1px solid #6366F140',
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6366F1', lineHeight: 1.4 }}>
              Rental
            </Typography>
          </Box>
        )}
        {unit?.current_residents && unit.current_residents.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <HouseLineIcon color="var(--mui-palette-success-main)" size={14} />
            <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              {unit.current_residents.length} resident{unit.current_residents.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
